// Re-export settings hooks for backward compatibility
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function useProfileSettings(user: any) {
  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      username: '',
      email: '',
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const loadProfile = async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setIsError(false);
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('display_name, username')
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const displayName = profile?.display_name || '';
      const spaceIndex = displayName.indexOf(' ');
      const firstName = spaceIndex > -1 ? displayName.substring(0, spaceIndex) : displayName;
      const lastName = spaceIndex > -1 ? displayName.substring(spaceIndex + 1) : '';

      form.reset({
        first_name: firstName,
        last_name: lastName,
        username: profile?.username || '',
        email: user.email || '',
      });
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setIsError(true);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [user?.id]);

  const onSubmit = async (data: any) => {
    if (!user?.id) return;
    try {
      setIsSubmitting(true);
      const displayName = `${(data.first_name || '').trim()} ${(data.last_name || '').trim()}`.trim();

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          display_name: displayName,
          username: data.username,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      if (data.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });
        if (emailError) throw emailError;
      }

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (err: any) {
      console.error('Error updating profile:', err);
      toast({
        title: "Error updating profile",
        description: err.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const retry = () => {
    setIsError(false);
    setError(null);
    loadProfile();
  };

  return {
    register: form.register,
    handleSubmit: form.handleSubmit,
    errors: form.formState.errors,
    onSubmit,
    isLoading,
    isError,
    error,
    isSubmitting,
    retry,
    formData: form.getValues(),
  };
}

export function useNotificationSettings(user: any) {
  const [settings, setSettings] = useState({
    notify_views: true,
    notify_likes: true,
    email_realtime: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const toggles = {
    notifyViews: (checked: boolean) =>
      setSettings((prev) => ({ ...prev, notify_views: checked })),
    notifyLikes: (checked: boolean) =>
      setSettings((prev) => ({ ...prev, notify_likes: checked })),
    emailRealtime: (checked: boolean) =>
      setSettings((prev) => ({ ...prev, email_realtime: checked })),
  };

  const retry = () => {
    setIsError(false);
    setError(null);
  };

  return {
    settings,
    toggles,
    isLoading,
    isSubmitting,
    isError,
    error,
    retry,
  };
}

export function useSecuritySettings(user: any) {
  const passwordForm = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const onSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      setError(null);

      // First verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: data.currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // If current password is correct, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Clear form and show success message
      passwordForm.reset();
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
      });
    } catch (err: any) {
      console.error("Error updating password:", err);
      setError(err);
      setIsError(true);
      toast({
        title: "Error updating password",
        description: err.message || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const twoFactor = {
    enabled: false,
    toggle: (enabled: boolean) => console.log("Two-factor toggle:", enabled),
    isLoading: false,
  };

  const retry = () => {
    setIsError(false);
    setError(null);
  };

  return {
    passwordForm: {
      ...passwordForm,
      onSubmit,
      isSubmitting: passwordForm.formState.isSubmitting,
    },
    twoFactor,
    isLoading,
    isError,
    error,
    retry,
  };
}

export function usePrivacySettings(user: any) {
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const actions = {
    exportData: async () => {
      setIsExporting(true);
      // Placeholder for actual implementation
      console.log("Exporting user data...");
      setTimeout(() => setIsExporting(false), 2000);
    },
    deleteAccount: async () => {
      setIsDeleting(true);
      // Placeholder for actual implementation
      console.log("Deleting account...");
      setTimeout(() => setIsDeleting(false), 2000);
    },
  };

  return {
    actions,
    isExporting,
    isDeleting,
    isError,
    error,
  };
}
